import { Box } from "@mui/material"
import type { ColumnDef } from "@tanstack/react-table"
import { useMemo } from "react"

import { PayFileApprovalActions } from "../components/PayFileApprovalActions"
import { PayFileRowActions } from "../components/PayFileRowActions"
import { PayFileStatusCell } from "../components/PayFileStatusCell"
import { PAY_FILE_TYPE_TO_LABEL } from "../constants"

import type { PayFileListFieldsFragment } from "@/graphql/codegen/graphql"
import { formatCurrencyWithCents } from "@/utils/currency"
import { formatDate } from "@/utils/date"

type UsePayFileColumnsReturn = ColumnDef<PayFileListFieldsFragment>[]

interface UsePayFileColumnsParams {
  onExport: (payFile: Pick<PayFileListFieldsFragment, "payFileId" | "referenceNumber">) => Promise<boolean>
  onSuccess: () => void
}

// Each sortable column's `id` must be the exact camelCase of its PayFileSortField member —
// useListPageBase round-trips it through toUpperSnakeCase, and an unmatched field silently no-ops.
// Those columns also need an `accessorFn`: TanStack's getCanSort() returns false without one, which
// would leave the header unclickable even though sorting is server-side. The trailing actions
// column is display-only, so it carries neither.
export const usePayFileColumns = ({ onExport, onSuccess }: UsePayFileColumnsParams): UsePayFileColumnsReturn =>
  useMemo<ColumnDef<PayFileListFieldsFragment>[]>(
    () => [
      {
        id: "scheduledPaymentDate",
        accessorFn: (payFile) => payFile.fromDate,
        header: "Scheduled Payment Date",
        cell: ({ row }) => formatDate(row.original.fromDate),
        enableSorting: true,
        size: 200,
      },
      {
        id: "totalAmount",
        accessorFn: (payFile) => payFile.totalAmount,
        header: "Total Amount",
        cell: ({ row }) => <span className='tabular-nums'>{formatCurrencyWithCents(row.original.totalAmount)}</span>,
        enableSorting: true,
      },
      {
        id: "paymentCount",
        accessorFn: (payFile) => payFile.paymentCount,
        header: "# of Payments",
        cell: ({ row }) => row.original.paymentCount,
        enableSorting: true,
      },
      {
        id: "type",
        accessorFn: (payFile) => payFile.type,
        header: "Type of File",
        // Falls back to the raw enum so a backend deploying a new PayFileType ahead of frontend
        // codegen shows something rather than an empty cell.
        cell: ({ row }) => PAY_FILE_TYPE_TO_LABEL[row.original.type] ?? row.original.type,
        enableSorting: true,
      },
      {
        id: "status",
        accessorFn: (payFile) => payFile.status,
        header: "Status",
        cell: ({ row }) => <PayFileStatusCell payFile={row.original} />,
        enableSorting: true,
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <Box className='flex items-center justify-end gap-x-3'>
            <PayFileApprovalActions payFile={row.original} onSuccess={onSuccess} />
            <PayFileRowActions row={row} onExport={onExport} onSuccess={onSuccess} />
          </Box>
        ),
        enableSorting: false,
      },
    ],
    [onExport, onSuccess]
  )
